import { UTCDate } from "@date-fns/utc";
import { UseQueryResult, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { TFunction } from "i18next";
import { createContext, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useJaktdataApi } from "~/src/api/jaktdataApi";
import { authProvider } from "~/src/appGlobals";
import { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import ButtonDefault from "~/src/components/Button/ButtonDefault";
import { syncJaktomrader } from "~/src/contexts/AppDataContext/JaktomradeSync";
import { AuthError } from "~/src/contexts/AuthContext/AuthProvider";
import { StorageContext } from "~/src/contexts/StorageContext/StorageProvider";
import UIContext from "~/src/contexts/UIContext/UIProvider";
import { OmradeType } from "~/src/helpers/types";
import { useClientState } from "~/src/hooks/useClientState";
import { useDialog } from "~/src/hooks/useDialog";
import { LocalStateful, useLocalStateful } from "~/src/hooks/useLocalState";
import { Stateful, useStateful } from "~/src/hooks/useStateful";
import { Favorittområde, JegerOpplysningerResponse, OmrådeData } from "../../api/types";
import {
  getJaktfeltQuery,
  getJaktområderQuery,
  getJegeropplysningerQuery,
  getValdQuery,
  getVillreinValdQuery,
} from "../../react-query/queries";
import { removeAllFormstates } from "../StorageContext/helpers";
import useJaktdataHelper from "../StorageContext/useJaktdataHelper";
import { syncIndivider } from "./IndividSync";
import { syncJaktdager } from "./JaktdagSync";

type SyncInfo = {
  omraderSynced: number;
  omraderFailed: number;
  jaktdagerSynced: number;
  jaktdagerFailed: number;
  individerSynced: number;
  individerFailed: number;
};

const syncDefault: SyncInfo = {
  omraderSynced: 0,
  omraderFailed: 0,
  jaktdagerSynced: 0,
  jaktdagerFailed: 0,
  individerSynced: 0,
  individerFailed: 0,
};

export type AppData = {
  jegeropplysninger: UseQueryResult<JegerOpplysningerResponse>;
  jaktfelt: UseQueryResult<OmrådeData>;
  vald: UseQueryResult<OmrådeData>;
  villreinvald: UseQueryResult<OmrådeData>;
  jaktområder: UseQueryResult<Favorittområde[]>;
  draftExists: Stateful<boolean | undefined>;
  sync: {
    allLoaded: boolean;
    isSynced: Stateful<boolean | undefined>;
    lastSynced: LocalStateful<string | undefined>;
    didTrySyncWithItems: Stateful<boolean | undefined>;
    isSyncing: Stateful<boolean>;
  };
};

const lastSyncedStorageKey = { id: "lastSynced" };
const lastSyncedDateFormat = "dd. LLL 'kl.' HH:mm";
const userDataQueriesStaleTime = 3 * 60 * 100; // 3 minutes
const areaQueriesStaleTime = 4 * 60 * 100; // 4 minutes

const AppDataContext = createContext<AppData>({} as AppData);
const DataProvider = (props: { children: JSX.Element }): JSX.Element => {
  const api = useJaktdataApi();
  const storage = useContext(StorageContext);
  const ui = useContext(UIContext);
  const isSynced = useStateful<boolean | undefined>(undefined);
  const isSyncing = useStateful<boolean>(false);
  const didTrySyncWithItems = useStateful<boolean | undefined>(undefined);
  const lastSynced = useLocalStateful<string | undefined>(lastSyncedStorageKey, undefined);
  const { refetchAllJaktdata } = useJaktdataHelper();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const shouldPerformLateTokenCheck = useStateful<boolean>(false);
  const LATE_TOKEN_CHECK_DURATION = 2000; // 2 seconds
  const expiredSessionDialog = useDialog({
    title: t("dialogs.sessionExpired.title"),
  });
  const clientState = useClientState();
  const isOnline = clientState === "Online";
  const syncInfo = useStateful<SyncInfo>({ ...syncDefault });
  const didMountRef = useRef(false);
  const draftExists = useStateful<boolean | undefined>(undefined);

  const performLateTokenCheck = () => {
    setTimeout(() => {
      shouldPerformLateTokenCheck.set(false);
      api.getClientState().then((state) => {
        if (state !== "Online") {
          expiredSessionDialog.show({
            message: t("pages.login.validation.tokenExpired.message"),
            title: t("pages.login.validation.tokenExpired.title"),
            buttons: (
              <ButtonDefault
                id="sessionExpiredDialogOkButton"
                label={t("actions.ok")}
                onClick={() => {
                  expiredSessionDialog.close();
                  navigate("/login");
                }}
              />
            ),
          });
        }
      });
    }, LATE_TOKEN_CHECK_DURATION);
  };

  useEffect(() => {
    if (clientState === "Offline") {
      isSynced.set(!storage.anyPending());
    } else if (clientState === "NotAuthenticated" && !authProvider.isRefreshing) {
      // verify and refresh
      authProvider.verify().catch((error: AuthError) => {
        // TODO: Refresh token slettes på serversiden når det er brukt, så det kan oppstå feil her hvis
        //       flere requester kjører samtidig.
        if (["TokenExpired", "TokenRejected", "Unknown"].includes(error.message)) {
          // HTTP 400 Bad Request eller 500 Internal Server Error
          // Hvis vi får 400 eller 500 fra /renew må vi sjekke om appen har fått et nytt token
          // som resultat av en annen request i mellomtiden. Denne sjekken gjøres etter LATE_TOKEN_CHECK_DURATION
          // for å unngå å kaste brukeren ut med en gang en renew forsøkes med et token som er "oppbrukt" på serveren.
          queryClient.cancelQueries();
          performLateTokenCheck();
        }
      });
    }
  }, [clientState]);

  function handleOfflineJaktområder() {
    let cachedOmrader: Favorittområde[] | undefined = queryClient.getQueryData(["jaktomrader"]);
    if (cachedOmrader != undefined) {
      const inStore = storage.getAll<Favorittområde>("PendingJaktomrade");
      inStore.forEach((o) => {
        const inCache = cachedOmrader?.find((i) => i.Områdekode);
        if (!inCache) {
          cachedOmrader?.push(o.data);
        } else if (o.operation === "delete") {
          cachedOmrader = cachedOmrader?.filter((om) => om.Områdekode != o.data.Områdekode);
        }
      });
      return cachedOmrader;
    }
    throw new Error("Ingen jaktområde-data i cache");
  }

  useEffect(() => {
    //Removes registration drafts on app restart

    removeAllFormstates();
    appData.draftExists.set(false);
  }, []);

  useEffect(() => {
    if (isOnline && isSynced.value === true) {
      const lastSyncedDateStr = format(
        parseISO(new UTCDate().toISOString()),
        lastSyncedDateFormat,
      ).toLowerCase();
      storage.add({ objectKey: lastSyncedStorageKey, data: lastSyncedDateStr });
      lastSynced.set(lastSyncedDateStr);
    }
  }, [isSynced.value, isOnline]);

  const jegeropplysninger = useQuery({
    meta: { url: "/jegeropplysninger" },
    queryKey: ["jegeropplysninger"],
    queryFn: getJegeropplysningerQuery,
    enabled: true,
    staleTime: isOnline ? userDataQueriesStaleTime : Infinity,
  });

  const jaktområder = useQuery({
    meta: { url: "/jaktomr%C3%A5der" },
    queryKey: ["jaktomrader"],
    queryFn: async () => {
      try {
        return getJaktområderQuery()
          .catch((_error) => {
            return handleOfflineJaktområder();
          })
          .then((omrader) => {
            const toDelete: Favorittområde[] = [];
            omrader.forEach((o) => {
              const isInStorage = storage.get<Favorittområde>(
                {
                  Områdekode: o.Områdekode,
                  Områdetype: o.Områdetype,
                },
                "PendingJaktomrade",
              );
              if (isInStorage) {
                if (isInStorage.syncStatus === "unconfirmed" && isOnline) {
                  storage.delete(
                    {
                      Områdekode: o.Områdekode,
                      Områdetype: o.Områdetype,
                    },
                    "PendingJaktomrade",
                  );
                }
                if (isInStorage.operation === "delete") {
                  toDelete.push(o);
                }
              }
            });

            const inStore = storage.getAll<Favorittområde>("PendingJaktomrade");
            return omrader
              .concat(
                inStore.filter((item) => item.operation !== "delete").map((item) => item.data), //adds instore creates
              )
              .filter(
                (o) =>
                  !toDelete.some(
                    (del) => del.Områdekode === o.Områdekode && del.Områdetype === o.Områdetype, //removes if there is a matching instore delete
                  ),
              )
              .filter((item) => !(item.JaktPåVillsvin && item.Områdetype === OmradeType.Kommune)) //does not show villsvin-områder
              .filter(
                (item, index, self) =>
                  index ===
                  self.findIndex(
                    (t) => t.Områdekode === item.Områdekode && t.Områdetype === item.Områdetype, //removes duplicates
                  ),
              );
          });
      } catch (_error) {
        return handleOfflineJaktområder();
      }
    },
    enabled: true,
    staleTime: userDataQueriesStaleTime,
    retry: 3,
  });

  const jaktfelt = useQuery({
    queryKey: ["jaktfelt"],
    queryFn: getJaktfeltQuery,
    enabled: true,
    staleTime: areaQueriesStaleTime,
  });

  const vald = useQuery({
    queryKey: ["vald"],
    queryFn: getValdQuery,
    enabled: true,
    staleTime: areaQueriesStaleTime,
  });

  const villreinvald = useQuery({
    queryKey: ["villreinvald"],
    queryFn: getVillreinValdQuery,
    enabled: true,
    staleTime: areaQueriesStaleTime,
  });

  const allLoaded = [jegeropplysninger, jaktområder].every((result) => result.isFetched);

  const appData: AppData = {
    jegeropplysninger,
    jaktfelt,
    vald,
    villreinvald,
    jaktområder,
    draftExists,
    sync: {
      allLoaded,
      isSynced,
      lastSynced,
      didTrySyncWithItems,
      isSyncing,
    },
  };

  useEffect(() => {
    if (!isOnline) {
      isSyncing.set(false);
    }
  }, [isOnline]);

  useEffect(() => {
    if (didTrySyncWithItems) {
      const res = buildSyncMessage(syncInfo.value, t);
      if (res) {
        ui.showSnackbar({
          message: res.message,
          severity: res.severity,
        });
        syncInfo.set({ ...syncDefault });
      }
    }
  }, [didTrySyncWithItems.value]);

  useEffect(() => {
    if (!isOnline || !allLoaded || !didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const acc: SyncInfo = { ...syncDefault };

    isSynced.set(!storage.anyPending());
    isSyncing.set(true);

    async function run() {
      try {
        isSynced.set(!storage.anyPending());
        isSyncing.set(true);

        // 1) Jaktområder
        {
          const result = await syncJaktomrader(storage);
          if (result) {
            acc.omraderSynced += result.omraderSynced;
            acc.omraderFailed += result.omraderNotSynced;
          }
          isSynced.set(!storage.anyPending());
        }

        // 2) Jaktdager (first pass)
        {
          const result = await syncJaktdager(storage);
          if (result) {
            acc.jaktdagerSynced += result.jaktdagerSynced;
            //don't set failed here, we use second pass failures
          }
          isSynced.set(!storage.anyPending());
        }

        // 3) Individer (first pass)
        {
          const result = await syncIndivider(storage);
          if (result) {
            acc.individerSynced += result.individerSynced;
            //don't set failed here, we use second pass failures
          }
          isSynced.set(!storage.anyPending());
        }

        // 4) Jaktdager again (sometimes connecting individer needs to be synced first)
        {
          const result = await syncJaktdager(storage);
          if (result) {
            acc.jaktdagerSynced += result.jaktdagerSynced;
            acc.jaktdagerFailed += result.jaktdagerNotSynced;
          }
        }

        // 5) Individer again
        {
          const result = await syncIndivider(storage);
          if (result) {
            acc.individerSynced += result.individerSynced;
            acc.individerFailed += result.individerNotSynced;
          }
          isSynced.set(!storage.anyPending());
        }

        // Finalize
        refetchAllJaktdata(appData);
        const successTotal = acc.omraderSynced + acc.jaktdagerSynced + acc.individerSynced;
        const failureTotal = acc.omraderFailed + acc.jaktdagerFailed + acc.individerFailed;

        syncInfo.set(acc);

        if (successTotal + failureTotal > 0) {
          if (acc.omraderSynced > 0) {
            jaktområder.refetch();
          }
          didTrySyncWithItems.set(true);
          setTimeout(() => didTrySyncWithItems.set(false), 100);
        }
      } finally {
        isSynced.set(!storage.anyPending());
        isSyncing.set(false);
      }
    }
    run();
  }, [jaktområder.data, isOnline]);

  return (
    <AppDataContext.Provider value={appData}>
      {expiredSessionDialog.element}
      {props.children}
    </AppDataContext.Provider>
  );
};

export { AppDataContext as default, DataProvider };

function buildSyncMessage(
  sync: SyncInfo,
  t: TFunction<"translation", undefined>,
): { message: string; severity: AlertVariant } | null {
  const successTotal = sync.omraderSynced + sync.jaktdagerSynced + sync.individerSynced;
  const failureTotal = sync.omraderFailed + sync.jaktdagerFailed + sync.individerFailed;

  if (successTotal + failureTotal === 0) {
    return null;
  }

  let severity: AlertVariant = AlertVariant.Success;
  let message = "";

  if (sync.omraderFailed > 0) {
    message +=
      t("sync.failure", {
        word: t("sync.type.ett"),
        type: t("sync.type.favorittområder").toLowerCase(),
        details: "",
      }) + "\n";
  } else if (sync.omraderSynced > 0) {
    message +=
      t("sync.success", {
        type: t("sync.type.favorittområder"),
      }) + "\n";
  }

  if (sync.jaktdagerFailed > 0) {
    message +=
      t("sync.failure", {
        word: t("sync.type.en"),
        type: t("sync.type.jaktdag").toLowerCase(),
        details: t("sync.syncErrorDetailsWhereJaktdag"),
      }) + "\n";
  } else if (sync.jaktdagerSynced > 0) {
    message +=
      t("sync.success", {
        type: t("sync.type.jaktdag"),
      }) + "\n";
  }

  if (sync.individerFailed > 0) {
    message +=
      t("sync.failure", {
        word: t("sync.type.ett"),
        type: t("sync.type.individ").toLowerCase(),
        details: t("sync.syncErrorDetailsWhereIndivid"),
      }) + "\n";
  } else if (sync.individerSynced > 0) {
    message +=
      t("sync.success", {
        type: t("sync.type.individ"),
      }) + "\n";
  }

  if (failureTotal === 0 && successTotal > 0) {
    severity = AlertVariant.Success;
  } else if (failureTotal > 0 && successTotal === 0) {
    severity = AlertVariant.Error;
  } else {
    severity = AlertVariant.Warning;
  }

  return { message, severity };
}
